import { useMemo, useContext, useState, useRef, useReducer, ChangeEvent } from 'react'
import AddIcon from '@mui/icons-material/Add'
import { Button, Checkbox, Divider, FormControlLabel, Tooltip } from '@mui/material'
import TextField from '@mui/material/TextField'
import { object, func, number, string, oneOfType } from 'prop-types'
import { useSelector } from 'react-redux'
import { PopoverWithSearch } from '@components/popover-with-search'
import { fieldSetContext } from '@contexts'
import { OrderFields } from './components/order-fields'
import { reducer } from './reducer'
import * as css from './styles'
import { ActionType, ConcatProps, FieldOption, State } from './types'
import { getFieldNameFromOptions, getInitialSelected } from './utils'
import { t } from '@lingui/core/macro'
import { RootState } from '@redux/store'

export function Concat({ form, setForm, fieldSetId, disabled, templateId }: ConcatProps) {
  const { byId } = useContext<any>(fieldSetContext)
  const templateFieldsById = useSelector((state: RootState) => state.Template.templateFieldsById)
  const [popoverOpen, setPopoverOpen] = useState(false)
  const anchorEl = useRef<HTMLButtonElement | null>(null)
  const hasAppliedFields = form?.concatenation_governance?.concatenation_governance_patterns?.length

  const options: FieldOption[] = useMemo(() => {
    if (fieldSetId) {
      return byId[fieldSetId].fields.filter(f => f.id !== form.id)
    }
    return Object.values(templateFieldsById)
      .map((tf: any) => tf.field)
      .filter(
        field =>
          field.id !== form.id &&
          field.segment_type !== 'variable' &&
          !field.is_pattern &&
          !field.segment_parent_field_id &&
          field.governance_type !== 'link',
      )[templateId]
  }, [byId, fieldSetId, form.id, templateFieldsById, templateId])

  const initialSelected = useMemo(
    () =>
      getInitialSelected(options, form.concatenation_governance.concatenation_governance_patterns),
    [options, form.concatenation_governance.concatenation_governance_patterns],
  )

  const initialState: State = {
    options,
    searchResults: [],
    searchTerm: '',
    selected: initialSelected,
  }

  const [state, dispatch] = useReducer(reducer, initialState)
  const { searchResults, searchTerm, selected } = state

  const noSearchResults = Boolean(searchTerm && searchResults.length === 0)
  const noFieldsSelected = !selected.length

  function handleSelectField(fieldId: string, checked: boolean) {
    dispatch({
      type: ActionType.setSelected,
      payload: { fieldId, checked },
    })
  }

  function handleClosePopover() {
    dispatch({
      type: ActionType.resetToInitialState,
      payload: { selected: initialSelected },
    })

    setPopoverOpen(false)
  }

  function handleSearch(e: ChangeEvent<HTMLInputElement>) {
    const { value } = e.target

    dispatch({
      type: ActionType.setSearchResults,
      payload: { searchTerm: value },
    })
  }

  function handleClearSearch() {
    dispatch({
      type: ActionType.setSearchResults,
      payload: { searchTerm: '' },
    })
  }

  function applySelection() {
    const fieldsWithNames = selected.map(field => ({
      ...field,
      concatenation_field_name: getFieldNameFromOptions(options, field.concatenation_field_id),
    }))

    setForm(prev => {
      return {
        ...prev,
        concatenation_governance: {
          ...prev.concatenation_governance,
          concatenation_governance_patterns: fieldsWithNames,
        },
      }
    })

    setPopoverOpen(false)
  }

  return (
    <>
      <TextField
        name="concat-separator"
        margin="dense"
        disabled={disabled}
        variant="outlined"
        placeholder='example: "-"'
        value={form.concatenation_governance.separator}
        onChange={e => {
          setForm({
            ...form,
            concatenation_governance: {
              ...form.concatenation_governance,
              separator: e.target.value,
            },
          })
        }}
        label={t`Separator`}
      />

      <p style={{ fontSize: '18px', marginBottom: '5px' }}>{t`Fields`}</p>
      <Button
        startIcon={<AddIcon />}
        color="primary"
        ref={anchorEl}
        onClick={() => {
          setPopoverOpen(true)
        }}
        variant="outlined"
        disabled={disabled}
        style={{
          marginTop: '8px',
          marginBottom: '4px',
          width: 'fit-content',
          alignItems: 'initial',
        }}
        data-test-atr="add-fields-button"
      >
        {t`Add fields`}
      </Button>
      <PopoverWithSearch
        open={popoverOpen}
        noDataMessage="We couldn't find any fields matching your search"
        anchorEl={anchorEl.current}
        handleOnClose={handleClosePopover}
        searchTerm={searchTerm}
        handleSearch={handleSearch}
        handleClearSearch={handleClearSearch}
        noSearchResults={noSearchResults}
        contentStyle={{
          minWidth: '368px',
          maxWidth: '368px',
          maxHeight: '72vh',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Divider />
        <div style={{ padding: '8px 12px', maxHeight: '20vh', overflow: 'auto' }}>
          {(searchResults.length ? searchResults : options).map((field: FieldOption) => (
            <div key={field.id}>
              <FormControlLabel
                label={field.name}
                control={
                  <Checkbox
                    color="secondary"
                    size="medium"
                    checked={selected.some(f => f.concatenation_field_id === field.id)}
                    onChange={() => {
                      handleSelectField(
                        field.id,
                        selected.some(f => f.concatenation_field_id === field.id),
                      )
                    }}
                    disableRipple
                  />
                }
              />
            </div>
          ))}
        </div>
        <Divider />
        <css.ButtonsContainer>
          <Button
            name="cancel"
            color="primary"
            variant="outlined"
            css={css.button}
            onClick={() => {
              setPopoverOpen(false)
            }}
          >
            {t`Cancel`}
          </Button>
          {noFieldsSelected ? (
            <Tooltip title={t`You must select at least one field`} placement="top" arrow>
              <span>
                <Button
                  name="apply"
                  disableElevation
                  color="primary"
                  variant="contained"
                  css={css.button}
                  onClick={applySelection}
                  disabled={disabled}
                >
                  {t`Add`}
                </Button>
              </span>
            </Tooltip>
          ) : (
            <Button
              name="apply"
              disableElevation
              color="primary"
              variant="contained"
              css={css.button}
              onClick={applySelection}
              disabled={disabled}
            >
              {t`Add`}
            </Button>
          )}
        </css.ButtonsContainer>
      </PopoverWithSearch>

      {hasAppliedFields ? (
        <OrderFields fields={selected} setForm={setForm} removeFieldFromState={handleSelectField} />
      ) : null}
    </>
  )
}

Concat.propTypes = {
  form: object.isRequired,
  setForm: func.isRequired,
  fieldSetId: oneOfType([number, string]).isRequired,
}
